import React, { useState, useMemo } from 'react'
import { Button, Input, Spin, message, Radio, Modal, Select, Alert } from 'antd'
import { api } from '../../../../../../../../../services'
import { useTranslation } from 'react-i18next'
import { ImagePreview } from '../../../../../../../../../components'
import { useDispatch } from 'react-redux'
import actions from '../../../../../../../../../store/actions'
import * as lessonBuilderActions from '../../../../../../../../../store/actions/lessonBuilder'
import nanoid from 'nanoid'

const { TextArea } = Input

const Infographics = ({
  onCancel,
  extractedText,
  previewData,
  pages,
  currentPage,
  lessonUpdate,
  setPreviewData
}) => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const [customPrompt, setCustomPrompt] = useState('')
  const [generatedImage, setGeneratedImage] = useState(null)
  const [isGenerating, setIsGenerating] = useState(false)
  const [isUploading, setIsUploading] = useState(false)
  const [showActionModal, setShowActionModal] = useState(false)
  const [selectedAction, setSelectedAction] = useState('replace')
  const [aspectRatio, setAspectRatio] = useState('1:1')

  const { handleLessonInput } = actions.lessonBuilder

  // Aspect ratio options
  const aspectRatioOptions = [
    { label: t('infographic:square'), value: '1:1' },
    { label: t('infographic:portrait'), value: '2:3' },
    { label: t('infographic:landscape'), value: '3:2' },
    { label: t('infographic:three_four_Portrait'), value: '3:4' },
    { label: t('infographic:four_three_landscape'), value: '4:3' },
    { label: t('infographic:four_five_portrait'), value: '4:5' },
    { label: t('infographic:four_five_landscape'), value: '5:4' },
    { label: t('infographic:mobile_portrait'), value: '9:16' },
    { label: t('infographic:widescreen'), value: '16:9' },
    { label: t('infographic:ultrawide'), value: '21:9' }
  ]

  // Extract page content (title and text) for display
  const pageContent = useMemo(() => {
    const selectedPage = previewData.find(page => page.pageSelected)

    if (!selectedPage || !selectedPage.data) {
      return { title: '', text: '' }
    }

    const titleText = selectedPage.data.find(
      element => element.type === 'TEXT' && element.display === 'HEADING'
    )?.params.content
    const textText = selectedPage.data.find(
      element => element.type === 'TEXT' && element.display !== 'HEADING'
    )?.params.content

    // Clean up the text
    let cleanedText = textText || ''

    if (
      cleanedText.includes('I could not find content related to this topic')
    ) {
      cleanedText = cleanedText.replace(
        'I could not find content related to this topic',
        ''
      )
    }

    cleanedText = cleanedText.replace(/\s+/g, ' ').trim()

    const cleanTextContent = htmlContent => {
      if (!htmlContent) return ''
      const parser = new DOMParser()
      const doc = parser.parseFromString(htmlContent, 'text/html')
      return doc.body.textContent
    }

    // unhtml tags
    const unhtmlText = cleanTextContent(cleanedText)
    const unhtmlTitle = cleanTextContent(titleText)

    return { title: unhtmlTitle, text: unhtmlText }
  }, [previewData, currentPage])

  const generateInfographic = async () => {
    // Check if page has meaningful content
    const hasPageContent = pageContent.title?.trim() || pageContent.text?.trim()
    const hasCustomPrompt = customPrompt?.trim()

    if (!hasPageContent && !hasCustomPrompt) {
      // Don't show message - the Alert component will handle this
      return
    }

    setIsGenerating(true)

    try {
      const response = await api.imageGeneration.generateNanoBananaImage({
        generate_infographic: true,
        course_title: pageContent.title || 'Infographic',
        course_content: pageContent.text,
        aspect_ratio: aspectRatio,
        user_prompt: customPrompt || '',
      })

      if (response && response.data) {
        // Check if response is a blob (direct image)
        // Blob detection: check if it's a Blob, or if it has blob-like properties
        const isBlob = response.data instanceof Blob ||
          (response.data.type && typeof response.data.size === 'number' && !response.data.body);

        if (isBlob) {
          // Check if the blob is actually a JSON error
          if (response.data.type === 'application/json') {
            const text = await new Promise((resolve, reject) => {
              const reader = new FileReader()
              reader.onload = () => resolve(reader.result)
              reader.onerror = reject
              reader.readAsText(response.data)
            })

            try {
              const json = JSON.parse(text)

              // Handle new format with direct URL
              if (json.url) {
                try {
                  const imgResponse = await fetch(json.url)
                  const blob = await imgResponse.blob()
                  const reader = new FileReader()
                  reader.onloadend = () => {
                    setGeneratedImage(reader.result)
                    message.success(t('infographic:generated'))
                  }
                  reader.onerror = () => {
                    throw new Error('Failed to read image data')
                  }
                  reader.readAsDataURL(blob)
                  return
                } catch (err) {
                  console.error('Error fetching generated image:', err)
                  throw new Error('Failed to fetch generated image from URL')
                }
              }

              if (json.errorMessage) {
                message.error(json.errorMessage)
                return
              }
            } catch (e) {
              console.error('Failed to parse error response:', e)
            }
          }

          // Convert blob to data URL
          const reader = new FileReader()
          reader.onloadend = () => {
            setGeneratedImage(reader.result)
            message.success(t('infographic:generated'))
          }
          reader.onerror = () => {
            throw new Error('Failed to read image data')
          }
          reader.readAsDataURL(response.data)
        } else if (response.data.body || response.data.errorMessage) {
          // Handle old format (JSON with base64)
          const { body, headers, errorMessage } = response.data

          if (errorMessage) {
            message.error(errorMessage)
            return
          }

          if (!body) {
            throw new Error('No image body in response')
          }

          const contentType = headers?.['Content-Type'] || 'image/png'
          const dataUrl = `data:${contentType};base64,${body}`

          setGeneratedImage(dataUrl)
          message.success(t('infographic:generated'))
        } else {
          console.error('Unexpected response format:', response.data)
          throw new Error('Unexpected response format')
        }
      } else {
        throw new Error('No image data received')
      }
    } catch (error) {
      console.error('Error generating infographic:', error)
      message.error(
        (error && error.message) || t('infographic:generation_failed')
      )
    } finally {
      setIsGenerating(false)
    }
  }

  const handleApplyClick = () => {
    if (!generatedImage) {
      message.error(t('infographic:no_image'))
      return
    }
    setShowActionModal(true)
  }

  const triggerUpdate = async data => {
    setPreviewData && setPreviewData(data)
    await dispatch(handleLessonInput('pages_landscape', data))
    await dispatch(handleLessonInput('pages', pages))

    if (lessonUpdate) {
      await lessonUpdate({ pages_landScape: data, pages })
    }
    onCancel()
  }

  const handleInfographicsApply = imageData => {
    // replace existing image or add new image
    // imageData is an object with { id, link, action }

    if (!imageData || !imageData.id || !imageData.link) {
      console.error('Invalid image data provided to handleInfographicsApply')
      return
    }

    const { id, link, action } = imageData

    let newData = []
    if (action === 'replace') {
      newData = previewData.map((page, index) => {
        if (page.pageSelected) {
          // Check if page has any IMAGE elements to replace
          const hasImage =
            page.data &&
            page.data.some(
              item => item.type === 'IMAGE' && item.display === 'IMAGE'
            )

          if (hasImage) {
            return {
              ...page,
              data: page.data.map(item => {
                if (item.type === 'IMAGE' && item.display === 'IMAGE') {
                  return {
                    ...item,
                    params: {
                      ...item.params,
                      content: link,
                      content_id: id
                    }
                  }
                }
                return item
              })
            }
          } else {
            // No image to replace, so add new image instead
            return {
              ...page,
              data: (page.data || []).concat({
                uid: nanoid(8),
                type: 'IMAGE',
                display: 'IMAGE',
                params: {
                  content: link,
                  content_id: id,
                  x: 0,
                  y: 0,
                  width: 300,
                  height: 300
                }
              })
            }
          }
        }
        return page
      })
    } else {
      // action === 'add'
      newData = previewData.map((page, index) => {
        if (page.pageSelected) {
          return {
            ...page,
            data: (page.data || []).concat({
              uid: nanoid(8),
              type: 'IMAGE',
              display: 'IMAGE',
              params: {
                content: link,
                content_id: id,
                x: 0,
                y: 0,
                width: 300,
                height: 300
              }
            })
          }
        }
        return page
      })
    }

    triggerUpdate(newData)
  }

  const handleConfirmApply = () => {
    if (!generatedImage) {
      message.error(t('infographic:no_image'))
      return
    }

    handleApply(generatedImage, selectedAction)
  }

  const getImageAsUploadedData = async dataUrl => {
    try {
      // Convert data URL to blob
      const response = await fetch(dataUrl)
      const blob = await response.blob()

      // Convert blob to base64
      const base64String = await new Promise((resolve, reject) => {
        const reader = new FileReader()
        reader.onloadend = () => resolve(reader.result)
        reader.onerror = error => reject(error)
        reader.readAsDataURL(blob)
      })

      const filename = `infographic-${Date.now()}.png`
      const fileData = {
        name: filename,
        filename: filename,
        title: filename,
        size: blob.size,
        croppedImage: base64String,
        type: blob.type || 'image/png',
        lastModifiedDate: new Date()
      }

      return fileData
    } catch (error) {
      console.error('Error converting image:', error)
      throw error
    }
  }

  const handleApply = async (image, action) => {
    if (!image) {
      message.error(t('infographic:no_image'))
      return
    }

    setIsUploading(true)

    try {
      // Convert data URL to file format
      const fileData = await getImageAsUploadedData(image)

      // Upload the image
      const uploadResponse = await dispatch(actions.library.upload(fileData))

      if (uploadResponse && uploadResponse.id && uploadResponse.link) {
        // Call handleInfographicsApply with the uploaded image data and action
        handleInfographicsApply({
          id: uploadResponse.id,
          link: uploadResponse.link,
          action
        })
        message.success(t(`infographic:${action}_success`))
      } else {
        throw new Error('Upload failed: Invalid response')
      }
    } catch (error) {
      console.error('Error uploading infographic:', error)
      message.error((error && error.message) || t('infographic:upload_failed'))
    } finally {
      setIsUploading(false)
    }
  }

  const handleCancelActionModal = () => {
    setShowActionModal(false)
    setSelectedAction('replace')
  }

  const handleCancel = () => {
    setCustomPrompt('')
    setGeneratedImage(null)
    setSelectedAction('replace')
    setAspectRatio('1:1')
    setShowActionModal(false)
    onCancel()
  }

  return (
    <>
      <Modal
        visible
        onCancel={handleCancel}
        footer={null}
        width={1000}
        centered
        title={t('infographic:title')}
        destroyOnClose
        maskClosable={false}
      >
        <div className="infographic-modal-content" style={{ padding: '8px 0' }}>
          {/* Validation Alert */}
          {(!(pageContent.title?.trim() || pageContent.text?.trim()) && !customPrompt?.trim()) && (
            <Alert
              message={t('v5:infographic_content_required')}
              description={t('v5:infographic_content_required_desc')}
              type="info"
              showIcon
              style={{ marginBottom: '24px', borderRadius: '8px', border: '1px solid #91caff', backgroundColor: '#e6f7ff' }}
            />
          )}

          {(!pageContent.title?.trim() && !pageContent.text?.trim() && customPrompt?.trim()) && (
            <Alert
              message={t('v5:infographic_using_custom_prompt')}
              description={t('v5:infographic_using_custom_prompt_desc')}
              type="warning"
              showIcon
              style={{ marginBottom: '24px', borderRadius: '8px' }}
            />
          )}

          <div style={{ display: 'flex', gap: '32px', alignItems: 'flex-start' }}>
            {/* Left Column: Controls & Context */}
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '24px' }}>

              {/* Source Context Card */}
              <div style={{
                backgroundColor: '#F9FAFB',
                borderRadius: '12px',
                border: '1px solid #E5E7EB',
                overflow: 'hidden'
              }}>
                <div style={{
                  padding: '12px 16px',
                  borderBottom: '1px solid #E5E7EB',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  backgroundColor: '#F3F4F6'
                }}>
                  <span style={{ fontSize: '12px', fontWeight: '600', color: '#4B5563', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                    {t('v5:infographic_source_context')}
                  </span>
                  {pageContent.title && (
                    <span style={{ fontSize: '12px', color: '#6B7280', maxWidth: '200px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {pageContent.title}
                    </span>
                  )}
                </div>

                <div style={{ padding: '16px', maxHeight: '180px', overflowY: 'auto' }}>
                  {(pageContent.text) ? (
                    <p style={{ fontSize: '13px', color: '#4B5563', lineHeight: '1.6', margin: 0 }}>
                      {pageContent.text}
                    </p>
                  ) : (
                    <div style={{ textAlign: 'center', padding: '12px', color: '#9CA3AF', fontSize: '13px', fontStyle: 'italic' }}>
                      {t('v5:infographic_no_source_content')}
                    </div>
                  )}
                </div>
              </div>

              {/* Controls */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>

                {/* Aspect Ratio */}
                <div>
                  <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#374151', marginBottom: '8px' }}>
                    {t('infographic:aspect_ratio') || 'Aspect Ratio'}
                  </label>
                  <Select
                    value={aspectRatio}
                    onChange={setAspectRatio}
                    disabled={isGenerating}
                    style={{ width: '100%' }}
                    size="large"
                    dropdownStyle={{ borderRadius: '8px' }}
                  >
                    {aspectRatioOptions.map(ratio => (
                      <Select.Option key={ratio.value} value={ratio.value}>
                        {ratio.label}
                      </Select.Option>
                    ))}
                  </Select>
                </div>

                {/* Custom Instructions */}
                <div>
                  <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#374151', marginBottom: '8px' }}>
                    {t('v5:infographic_custom_instructions')}
                    <span style={{ fontWeight: '400', color: '#9CA3AF', marginLeft: '4px' }}>(Optional)</span>
                  </label>
                  <TextArea
                    placeholder={t('v5:infographic_prompt_placeholder_new')}
                    value={customPrompt}
                    onChange={e => setCustomPrompt(e.target.value)}
                    autoSize={{ minRows: 3, maxRows: 6 }}
                    disabled={isGenerating}
                    style={{ borderRadius: '8px', padding: '10px 12px', fontSize: '14px' }}
                  />
                </div>

                <Button
                  type="primary"
                  size="large"
                  onClick={generateInfographic}
                  loading={isGenerating}
                  disabled={
                    !(pageContent.title?.trim() || pageContent.text?.trim()) &&
                    !customPrompt?.trim()
                  }
                  block
                  style={{
                    height: '48px',
                    fontSize: '15px',
                    fontWeight: '600',
                    borderRadius: '8px',
                    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
                    marginTop: '8px'
                  }}
                >
                  {isGenerating
                    ? t('infographic:generating')
                    : generatedImage
                      ? t('infographic:regenerate')
                      : t('v5:infographic_generate_btn')}
                </Button>
              </div>
            </div>

            {/* Right Column: Preview */}
            <div style={{ flex: 1, minWidth: '300px' }}>
              <div style={{
                height: '100%',
                minHeight: '520px',
                backgroundColor: '#F3F4F6',
                borderRadius: '16px',
                border: '1px dashed #D1D5DB',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                position: 'relative',
                overflow: 'hidden'
              }}>
                {isGenerating ? (
                  <div style={{ textAlign: 'center' }}>
                    <Spin size="large" />
                    <p style={{ marginTop: '16px', color: '#6B7280', fontWeight: '500' }}>{t('v5:infographic_creating_magic')}</p>
                  </div>
                ) : generatedImage ? (
                  <div style={{ width: '100%', height: '100%', padding: '16px', display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column' }}>
                    <ImagePreview
                      src={generatedImage}
                      alt="Generated Infographic"
                      style={{
                        maxWidth: '100%',
                        maxHeight: '480px',
                        borderRadius: '8px',
                        boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)',
                        objectFit: 'contain'
                      }}
                    />
                    <div style={{ marginTop: '16px', display: 'flex', gap: '12px' }}>
                      <Button onClick={() => setGeneratedImage(null)} style={{ borderRadius: '6px' }}>
                        {t('v5:infographic_discard')}
                      </Button>
                      <Button type="primary" onClick={handleApplyClick} style={{ borderRadius: '6px' }}>
                        {t('v4:apply_changes')}
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div style={{ textAlign: 'center', padding: '32px', color: '#9CA3AF' }}>
                    <div style={{ fontSize: '48px', marginBottom: '16px', opacity: 0.5 }}>🎨</div>
                    <h3 style={{ fontSize: '16px', fontWeight: '600', color: '#6B7280', marginBottom: '8px' }}>
                      {t('v5:infographic_preview_area')}
                    </h3>
                    <p style={{ fontSize: '13px', maxWidth: '240px', margin: '0 auto' }}>
                      {t('v5:infographic_preview_desc')}
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Footer Actions (Hidden since we moved Apply to preview area, but keeping Cancel for modal consistency if needed, or we can remove the footer prop from Modal and put it here) */}
          {/* We are using footer={null} in the Modal props, so we need a way to close if not generating.
              The 'X' top right works, but a Cancel button is good practice.
              Since we have a 2-col layout, maybe put it bottom left or just rely on the top X?
              Let's add a subtle Cancel text button bottom left if needed, or just leave it clean.
              Actually, let's keep the Cancel button at the very bottom right of the left column if we want,
              OR just rely on the modal close. The previous design had a footer div.
              Let's add a simple footer row below the 2 columns for "Cancel".
          */}
          <div style={{ marginTop: '24px', paddingTop: '16px', borderTop: '1px solid #F3F4F6', display: 'flex', justifyContent: 'flex-end' }}>
            <Button onClick={handleCancel} type="text" style={{ color: '#6B7280' }}>
              {t('buttons:cancel')}
            </Button>
          </div>
        </div>
      </Modal>

      {/* Action Selection Modal */}
      <Modal
        visible={showActionModal}
        onCancel={handleCancelActionModal}
        footer={null}
        width={500}
        centered
        title={t('infographic:apply_question')}
      >
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '20px',
            paddingTop: '8px'
          }}
        >
          <Radio.Group
            value={selectedAction}
            onChange={e => setSelectedAction(e.target.value)}
            style={{ width: '100%' }}
          >
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '16px'
              }}
            >
              <Radio value="replace">
                <div>
                  <div
                    style={{
                      fontWeight: '500',
                      fontSize: '15px',
                      marginBottom: '4px'
                    }}
                  >
                    {t('infographic:replace_option')}
                  </div>
                  <div
                    style={{
                      fontSize: '13px',
                      color: '#8c8c8c',
                      marginLeft: '24px'
                    }}
                  >
                    {t('infographic:replace_the_existing_image_on_the_page')}
                  </div>
                </div>
              </Radio>
              <Radio value="add">
                <div>
                  <div
                    style={{
                      fontWeight: '500',
                      fontSize: '15px',
                      marginBottom: '4px'
                    }}
                  >
                    {t('infographic:add_option')}
                  </div>
                  <div
                    style={{
                      fontSize: '13px',
                      color: '#8c8c8c',
                      marginLeft: '24px'
                    }}
                  >
                    {t('infographic:add_this_infographic')}
                  </div>
                </div>
              </Radio>
            </div>
          </Radio.Group>

          <div
            style={{
              display: 'flex',
              gap: '12px',
              justifyContent: 'flex-end',
              paddingTop: '12px',
              borderTop: '1px solid #f0f0f0'
            }}
          >
            <Button size="large" onClick={handleCancelActionModal}>
              {t('buttons:cancel')}
            </Button>
            <Button
              type="primary"
              size="large"
              onClick={handleConfirmApply}
              loading={isUploading}
              disabled={isUploading}
              style={{
                minWidth: '120px'
              }}
            >
              {t('buttons:confirm') || 'Confirm'}
            </Button>
          </div>
        </div>
      </Modal>
    </>
  )
}

export default Infographics
